import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Brand, ThemeToggle, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LogOut, Trophy, Users, Activity, CheckCircle2, Archive, Trash2, Ban, RotateCcw, Save, CreditCard } from "lucide-react";

const TOURNAMENT_TYPES = [
  ["single_elimination", "Single Elimination"],
  ["round_robin", "Round Robin"],
  ["group_elimination", "Group Stage + Elimination"],
  ["double_elimination", "Double Elimination"],
  ["swiss", "Swiss System"],
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [users, setUsers] = useState([]);
  const [tournamentFilter, setTournamentFilter] = useState("");
  const [hostFilter, setHostFilter] = useState("");
  const [payments, setPayments] = useState(null);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/tournaments").then((r) => setTournaments(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/payment-settings").then((r) => setPayments(r.data));
  };
  useEffect(() => { load(); }, []);

  const delT = async (id) => { await api.delete(`/admin/tournaments/${id}`); toast.success("Tournament removed"); load(); };
  const toggleU = async (id) => { const r = await api.post(`/admin/users/${id}/toggle`); toast.success(r.data.disabled ? "User disabled" : "User enabled"); load(); };
  const setVip = async (id, vip) => { await api.put(`/admin/users/${id}/vip`, { vip }); toast.success(vip ? "Host marked as VIP" : "VIP status removed"); load(); };
  const delU = async (id) => { const r = await api.delete(`/admin/users/${id}`); toast.success(`Host deleted${r.data.deleted_tournaments ? ` with ${r.data.deleted_tournaments} tournament(s)` : ""}`); load(); };
  const setPayment = (k, v) => setPayments((p) => ({ ...p, [k]: v }));
  const setProduct = (k, v) => setPayments((p) => ({ ...p, stripe_product_ids: { ...(p.stripe_product_ids || {}), [k]: v } }));
  const savePayments = async () => { await api.put("/admin/payment-settings", payments); toast.success("Payment settings saved"); load(); };

  const cards = stats ? [
    { icon: Users, l: "Hosts", v: stats.hosts },
    { icon: Trophy, l: "Tournaments", v: stats.tournaments },
    { icon: Activity, l: "Active", v: stats.active },
    { icon: CheckCircle2, l: "Completed", v: stats.completed },
    { icon: Archive, l: "Archived", v: stats.archived },
    { icon: Users, l: "Teams", v: stats.teams },
  ] : [];

  const tournamentQuery = tournamentFilter.trim().toLowerCase();
  const hostQuery = hostFilter.trim().toLowerCase();
  const filteredTournaments = tournamentQuery ? tournaments.filter((t) => [t.name, t.date, t.status, t.location]
    .some((v) => String(v || "").toLowerCase().includes(tournamentQuery))) : tournaments;
  const filteredUsers = hostQuery ? users.filter((u) => [u.name, u.email]
    .some((v) => String(v || "").toLowerCase().includes(hostQuery))) : users;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-background/70 px-5 py-3 backdrop-blur-xl">
        <div className="flex items-center gap-3"><Brand onClick={() => nav("/")} /><span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-bold text-accent">ADMIN</span></div>
        <div className="flex items-center gap-2"><ThemeToggle /><Button variant="ghost" size="icon" onClick={() => { logout(); nav("/"); }} data-testid="logout-button"><LogOut className="h-5 w-5" /></Button></div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Platform admin</h1>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {cards.map((c) => (
            <Card key={c.l} className="p-4" data-testid={`admin-stat-${c.l.toLowerCase()}`}>
              <c.icon className="h-5 w-5 text-primary" />
              <div className="mt-2 font-heading text-2xl font-black">{c.v}</div>
              <div className="text-label text-muted-foreground">{c.l}</div>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="tournaments" className="mt-8">
          <TabsList>
            <TabsTrigger value="tournaments" data-testid="admin-tab-tournaments">Tournaments</TabsTrigger>
            <TabsTrigger value="hosts" data-testid="admin-tab-hosts">Hosts</TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="tournaments" className="mt-4 space-y-2">
            <Input value={tournamentFilter} onChange={(e) => setTournamentFilter(e.target.value)} placeholder="Filter tournaments by name, date, status or location…" data-testid="admin-tournament-filter" />
            {filteredTournaments.map((t) => (
              <Card key={t.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`admin-tournament-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className={`font-semibold ${["completed", "archived"].includes(t.status) ? "strike-through-name" : ""}`}>{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.host_email} · {t.team_count} teams</div>
                </div>
                <StatusBadge status={t.status} />
                <Button variant="outline" size="sm" onClick={() => window.open(`/t/${t.public_code}`, "_blank")}>View</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`admin-delete-tournament-${t.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Remove tournament?</AlertDialogTitle><AlertDialogDescription>This permanently deletes "{t.name}" and all its data.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => delT(t.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </Card>
            ))}
            {tournaments.length === 0 && <p className="text-muted-foreground">No tournaments yet.</p>}
            {tournaments.length > 0 && filteredTournaments.length === 0 && <p className="text-muted-foreground">No tournaments match your filter.</p>}
          </TabsContent>

          <TabsContent value="hosts" className="mt-4 space-y-2">
            <Input value={hostFilter} onChange={(e) => setHostFilter(e.target.value)} placeholder="Filter hosts by name or email…" data-testid="admin-host-filter" />
            {filteredUsers.map((u) => (
              <Card key={u.id} className="flex items-center gap-3 p-3" data-testid={`admin-user-${u.id}`}>
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 font-bold text-primary">{(u.name || u.email)[0]?.toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 font-semibold">{u.name} {u.role === "admin" && <span className="rounded bg-accent/15 px-1.5 text-[10px] font-bold text-accent">ADMIN</span>}{u.vip && <span className="rounded bg-purple-100 px-1.5 text-[10px] font-bold text-purple-700 dark:bg-purple-950 dark:text-purple-300">VIP</span>}{u.disabled && <span className="rounded bg-muted px-1.5 text-[10px] font-bold text-muted-foreground">DISABLED</span>}</div>
                  <div className="text-xs text-muted-foreground">{u.email} · {u.tournaments} tournaments</div>
                </div>
                {u.role !== "admin" && (
                  <>
                    <label className="flex items-center gap-2 text-sm font-medium" data-testid={`admin-vip-user-${u.id}`}>
                      VIP <Switch checked={!!u.vip} onCheckedChange={(checked) => setVip(u.id, !!checked)} />
                    </label>
                    <Button variant="outline" size="sm" onClick={() => toggleU(u.id)} data-testid={`admin-toggle-user-${u.id}`}>
                      {u.disabled ? <><RotateCcw className="mr-1 h-3.5 w-3.5" /> Enable</> : <><Ban className="mr-1 h-3.5 w-3.5" /> Disable</>}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`admin-delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete host?</AlertDialogTitle>
                          <AlertDialogDescription>This permanently deletes {u.name || u.email}, their account, and all {u.tournaments} tournament(s) with teams, matches and results. This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => delU(u.id)} className="bg-destructive text-destructive-foreground" data-testid={`confirm-delete-user-${u.id}`}>Delete host</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </Card>
            ))}
            {users.length > 0 && filteredUsers.length === 0 && <p className="text-muted-foreground">No hosts match your filter.</p>}
          </TabsContent>

          <TabsContent value="payments" className="mt-4">
            {payments && (
              <Card className="space-y-5 p-6">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <h2 className="font-heading text-xl font-bold">Stripe configuration</h2>
                </div>

                <label className="flex items-center justify-between gap-4 rounded-lg border p-3" data-testid="stripe-enabled">
                  <div>
                    <div className="font-medium">Enable payments</div>
                    <div className="text-sm text-muted-foreground">When enabled, hosts must pay once before activating a tournament.</div>
                  </div>
                  <Switch checked={!!payments.stripe_enabled} onCheckedChange={(v) => setPayment("stripe_enabled", !!v)} />
                </label>

                <div>
                  <Label>Stripe publishable key</Label>
                  <Input value={payments.stripe_publishable_key || ""} onChange={(e) => setPayment("stripe_publishable_key", e.target.value)} className="mt-1.5" data-testid="stripe-publishable-key" />
                </div>

                <div className="space-y-3">
                  <Label>Tournament Product ID per tournament type</Label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {TOURNAMENT_TYPES.map(([id, label]) => (
                      <div key={id}>
                        <Label className="text-xs text-muted-foreground">{label}</Label>
                        <Input value={(payments.stripe_product_ids || {})[id] || ""} onChange={(e) => setProduct(id, e.target.value)} className="mt-1.5" placeholder="prod_..." data-testid={`stripe-product-${id}`} />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Webhook signing secret</Label>
                  <Input value={payments.stripe_webhook_secret || ""} onChange={(e) => setPayment("stripe_webhook_secret", e.target.value)} className="mt-1.5" placeholder="whsec_..." data-testid="stripe-webhook-secret" />
                </div>

                <Button onClick={savePayments} data-testid="save-payment-settings"><Save className="mr-1.5 h-4 w-4" /> Save payment settings</Button>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
